import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { RotateCcw, TriangleAlert } from 'lucide-react'
import { apiClient, ApiError } from '@/lib/api'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Badge, Modal } from '@/components/ui/primitives'
import { useSession } from '@/stores/session'
import { Permission } from '@/lib/permissions'
import { t } from '@/i18n'

/** The kinds of record that can be undone, and the route that undoes each. */
export const REVERSIBLE = {
  expense: { path: 'expenses', noun: 'expense' },
  cashMovement: { path: 'cash-movements', noun: 'drawer movement' },
  safeTransaction: { path: 'safe-transactions', noun: 'safe transaction' },
  accountPayment: { path: 'account-payments', noun: 'account payment' },
  waste: { path: 'waste', noun: 'write-off' },
  goodsReceipt: { path: 'goods-receipts', noun: 'delivery' },
  refund: { path: 'refunds', noun: 'refund' },
} as const

export type ReversibleKind = keyof typeof REVERSIBLE

/**
 * Undoes one recorded transaction.
 *
 * Asks for a reason before it will do anything, because the reason is the control: an entry
 * taken back out without one is indistinguishable from an entry nobody was meant to question.
 * The server refuses a blank one too — this only saves the round trip.
 */
export function ReverseDialog({
  kind,
  id,
  what,
  onClose,
  invalidate = [],
}: {
  kind: ReversibleKind
  /** Null closes the dialog. */
  id: number | null
  /** How the thing reads to a human, e.g. "Rent — $120.00". */
  what: string
  onClose: () => void
  /** Query keys to refresh once it lands. */
  invalidate?: string[]
}) {
  const queryClient = useQueryClient()
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (id !== null) {
      setReason('')
      setError(null)
    }
  }, [id])

  const reverse = useMutation({
    mutationFn: () =>
      apiClient.post<{ entity: string; id: number; summary: string }>(
        `/api/reversals/${REVERSIBLE[kind].path}/${id}`,
        { reason: reason.trim() },
      ),
    onSuccess: async () => {
      await Promise.all(
        invalidate.map((key) => queryClient.invalidateQueries({ queryKey: [key] })),
      )
      onClose()
    },
    onError: (caught) =>
      setError(
        caught instanceof ApiError
          ? (caught.detail ?? caught.message)
          : 'Could not reverse that.',
      ),
  })

  return (
    <Modal
      open={id !== null}
      onClose={onClose}
      title={`Reverse this ${REVERSIBLE[kind].noun}`}
      description={what}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button
            variant="danger"
            onClick={() => reverse.mutate()}
            disabled={reverse.isPending || reason.trim().length === 0}
          >
            {reverse.isPending ? 'Reversing…' : 'Reverse it'}
          </Button>
        </>
      }
    >
      <Input
        label={t('Why')}
        value={reason}
        onChange={(event) => setReason(event.target.value)}
        placeholder={t('What went wrong')}
        autoFocus
      />

      <p className="surface-sunken mt-4 rounded-lg px-3 py-2 text-xs text-[var(--text-secondary)]">
        The original entry is kept and marked reversed, with your name against it. The money or
        stock is put back by a second entry rather than by deleting the first, so the history
        still shows what happened.
      </p>

      {error && (
        <p role="alert" className="mt-3 rounded-lg bg-danger-500/10 px-3 py-2 text-sm text-danger-500">
          <TriangleAlert className="mr-1 inline h-4 w-4" />
          {error}
        </p>
      )}
    </Modal>
  )
}

/**
 * The button that opens it. Renders nothing at all without the right, and nothing once the
 * row is already reversed — there is no second thing to undo.
 */
export function ReverseButton({
  onClick,
  isReversed,
  label,
}: {
  onClick: () => void
  isReversed?: boolean
  label: string
}) {
  const canReverse = useSession((state) => state.can)(Permission.ReverseTransaction)

  if (!canReverse || isReversed) return null

  return (
    <Button variant="ghost" size="icon" aria-label={label} onClick={onClick}>
      <RotateCcw className="h-4 w-4" />
    </Button>
  )
}

/** The badge shown in place of the button once something has been reversed. */
export function ReversedBadge({ reason, by }: { reason?: string | null; by?: string | null }) {
  // The title sits on a wrapper rather than the Badge, which takes no DOM props of its own.
  return (
    <span title={[reason, by].filter(Boolean).join(' · ') || undefined}>
      <Badge tone="neutral">{t('Reversed')}</Badge>
    </span>
  )
}
